// src/components/Products/SpeciesLearnGrid.jsx
// #103 zero-inventory graceful degradation — LEARN mode for the /frogs
// and /frogs/:genus browse pages. Mounted ONLY when the products fetch
// returned zero products (full/lean weeks never render this), it swaps
// the empty grid for species cards from the public species list: cover
// photo, name, a "Learn" link into the care guide, and a per-species
// "Notify me" that opens the anonymous arrivals capture inline.
// If the species fetch fails or nothing qualifies, `fallback` (the
// page's original empty-state) renders instead.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { getPublicSpecies } from '../../services/api';
import { createSpeciesSlug } from '../../utils/slugUtils';
import ArrivalNotifyForm from '../common/ArrivalNotifyForm';
import SkeletonCardGrid from '../common/SkeletonCardGrid';
import { NO_PHOTO_SRC } from '../../constants/noPhoto';
import { showNoPhoto } from '../../utils/imageResolver';
import '../../styles/products.css';
import { formatSpeciesName } from '../../utils/scientificName';
import { isStorefrontSpeciesType } from '../../constants/speciesTypes';

// `wayOn` (batch 34 C6): an optional "way on" row under the learn header —
// /geckos passes "Browse frogs" + "Care guides" so a category with no stock
// is never a dead end.
const SpeciesLearnGrid = ({ genus = null, speciesType = null, fallback = null, wayOn = null }) => {
  const [speciesList, setSpeciesList] = useState(null); // null = loading
  const [notifyOpenId, setNotifyOpenId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const results = await getPublicSpecies(genus ? { genus } : {});
        if (!cancelled) setSpeciesList(results);
      } catch (err) {
        // Non-fatal: fall back to the page's original empty state.
        console.warn('[SpeciesLearnGrid] species load failed:', err?.message || err);
        if (!cancelled) setSpeciesList([]);
      }
    })();
    return () => { cancelled = true; };
  }, [genus]);

  /* speciesType (0 = frogs, matching getAnimalTypes ids) keeps the /frogs
     learn grid frog-only; the genus page is already scoped by its genus
     param. Deps (`speciesList`, `speciesType`) are declared above this
     hook — project rule: the production minifier exposes TDZ that dev
     mode tolerates. */
  /* I-03 close 1c (operator 2026-10-24): a species set "not sold here"
     gets NO place on an "Available now" page and no "Notify me" — its
     Learn lives on the Care Guides index. A REPTILE ("Other reptile")
     species never shows here either: that type has no shop page. */
  const visibleSpecies = useMemo(() => {
    if (!Array.isArray(speciesList)) return [];
    return speciesList.filter(
      (sp) => (speciesType == null || sp.species_type === speciesType)
        && sp.not_sold_here !== true
        && isStorefrontSpeciesType(sp.species_type)
    );
  }, [speciesList, speciesType]);

  if (speciesList === null) {
    return <SkeletonCardGrid count={6} />;
  }

  if (visibleSpecies.length === 0) {
    return fallback;
  }

  return (
    <div className="species-learn-mode">
      <div className="species-learn-header">
        <h2 className="species-learn-title">
          Nothing available right now &mdash; meet the species
        </h2>
        <p className="species-learn-subtitle">
          Read up while we&rsquo;re between arrivals, and get an email the
          moment new animals land.
        </p>
        {wayOn}
      </div>
      <Row className="g-4">
        {visibleSpecies.map((sp) => {
          const sciName = formatSpeciesName(sp, { withLocality: false });
          const displayName = sp.common_names?.[0] || sciName;
          const imgUrl = sp.cover_display_url || sp.cover_thumb_url;
          return (
            <Col key={sp.id} xs={12} sm={6} md={4}>
              <div className="species-learn-card">
                <div className="species-learn-card-img-wrap">
                  <img
                    src={imgUrl || NO_PHOTO_SRC}
                    data-no-photo={imgUrl ? undefined : '1'}
                    alt={displayName}
                    loading="lazy"
                    className="species-learn-card-img"
                    onError={(e) => showNoPhoto(e.currentTarget)}
                  />
                </div>
                <div className="species-learn-card-body">
                  <h3 className="species-learn-card-title">{displayName}</h3>
                  <p className="species-learn-card-sci">
                    <em>{sciName}</em>
                    {sp.locality && <>&nbsp;&ldquo;{sp.locality}&rdquo;</>}
                  </p>
                  <div className="species-learn-card-actions">
                    {/* Batch 34 C1: "Learn" repeats on every card, so it is a
                        secondary — a repeated per-card act is never a primary. */}
                    {sp.care_guide_available && (
                      <Link
                        to={`/care-guides/${createSpeciesSlug(sp)}`}
                        className="ff-btn ff-btn--secondary ff-btn--sm"
                      >
                        Learn
                      </Link>
                    )}
                    <button
                      type="button"
                      className="ff-btn ff-btn--secondary ff-btn--sm"
                      aria-expanded={notifyOpenId === sp.id}
                      onClick={() => setNotifyOpenId(
                        notifyOpenId === sp.id ? null : sp.id
                      )}
                    >
                      Notify me
                    </button>
                  </div>
                  {notifyOpenId === sp.id && (
                    <ArrivalNotifyForm
                      compact
                      speciesId={sp.id}
                      prompt={`Get an email when ${displayName} are available`}
                      className="species-learn-card-notify"
                    />
                  )}
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default SpeciesLearnGrid;
